import { Box, Button, Divider, Flex, Menu, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClients } from 'src/clients/hooks/useClients';
import { Client } from 'src/admin/users/types';
import classes from './UnderlineTabs.module.css';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { formatName } from 'src/utils/nameFormatting';

type ClientSearchProps = {
  width?: number;
  prefix?: string;
};

type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
};

export function ClientSearch({ width, prefix }: ClientSearchProps) {
  return prefix ? (
    <Flex gap="xxs" align="center">
      <Text size={'xxl'} fw={600}>
        {prefix}
      </Text>
      <Box pt="xxs">
        <ClientSearchMenu width={width} />
      </Box>
    </Flex>
  ) : (
    <ClientSearchMenu />
  );
}

function ClientSearchMenu({ width }: ClientSearchProps) {
  const { clients } = useClients();
  const { clientId, client } = useCurrentClient();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Client[]>([]);
  const [menuOpened, setMenuOpened] = useState(false);
  const theme = useMantineTheme();
  const clientName = formatName(client?.firstName, client?.lastName);
  const { pathname } = useLocation();

  useEffect(() => {
    if (search.length > 0) {
      const searchFields = ['firstName', 'lastName', 'emailAddress'] as const;
      setResults(
        clients
          .filter(c =>
            searchFields.some(field =>
              c[field]?.toLowerCase().includes(search.toLowerCase() ?? ''),
            ),
          )
          .slice(0, 3),
      );
    } else {
      setResults([]);
    }
  }, [search]);

  return (
    <Menu
      position="bottom-start"
      shadow="md"
      opened={menuOpened}
      onClose={() => setMenuOpened(false)}
      classNames={{
        dropdown: classes.clientSearchDropdown,
      }}
    >
      <Menu.Target>
        <Button
          variant="subtle"
          style={{
            fontWeight: 600,
            color: theme.colors.brand[6],
            fontSize: theme.fontSizes.xxl,
          }}
          px={0}
          styles={{
            root: {
              width,
            },
            label: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
          }}
          rightSection={
            menuOpened ? (
              <IconChevronUp size={18} color={theme.colors.brand[7]} />
            ) : (
              <IconChevronDown size={18} color={theme.colors.brand[7]} />
            )
          }
          onClick={() => setMenuOpened(o => !o)}
        >
          {clientName || 'Select Client'}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Flex direction="column" gap="xxs" w={220}>
          <SearchInput value={search} onChange={setSearch} />
          {results.length > 0 && <Divider color={'gray.0'} />}
          {results.map(clientResult => {
            const href = pathname.replace(clientId, clientResult.id);
            return (
              <Menu.Item
                component={Link}
                key={clientResult.id}
                to={href}
                onClick={() => {
                  setSearch('');
                  setMenuOpened(false);
                }}
              >
                {formatName(clientResult.firstName, clientResult.lastName)}
              </Menu.Item>
            );
          })}
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}

function SearchInput({ value, onChange }: SearchInputProps) {
  function focusInput(el: HTMLInputElement | null) {
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
      });
    }
  }

  return (
    <TextInput
      ref={focusInput}
      placeholder="Search clients"
      value={value}
      onChange={e => onChange(e.target.value)}
      classNames={{
        input: classes.clientSearchInput,
      }}
      rightSection={<IconSearch color={'#E5E5E5'} />}
      autoFocus
    />
  );
}
